import * as AuthSession from 'expo-auth-session';
import * as QueryParams from 'expo-auth-session/build/QueryParams';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import { logger } from '../logger';
import { supabase } from '../supabase';

// Ensure web browser auth sessions can complete
WebBrowser.maybeCompleteAuthSession();

class GoogleAuth {
  private redirectUri: string = '';

  constructor() {
    // Only generate redirect URI on client side
    if (typeof window !== 'undefined' || Platform.OS !== 'web') {
      // Use makeRedirectUri to generate proper redirect URL
      this.redirectUri = AuthSession.makeRedirectUri({
        scheme: 'openstudiojazz', // Match the scheme in app.config.ts
        path: 'auth/callback',
      });
      __DEV__ && logger.debug('OAuth Redirect URI:', this.redirectUri);
    }
  }

  async signInWithGoogle() {
    try {
      if (Platform.OS === 'web') {
        // Web platform: use Supabase built-in OAuth
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });

        if (error) throw error;
        return { data, error: null };
      } else {
        // Mobile platforms: initiate OAuth and open web browser
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: this.redirectUri,
            skipBrowserRedirect: true, // We'll handle the browser opening
          },
        });

        if (error) throw error;

        // Open the OAuth URL in the browser
        if (data?.url) {
          const result = await WebBrowser.openAuthSessionAsync(
            data.url,
            this.redirectUri,
            {
              showInRecents: true,
            }
          );

          if (result.type === 'success' && result.url) {
            // Parse the URL and create session
            await this.createSessionFromUrl(result.url);
          }
        }

        return { data, error: null };
      }
    } catch (error) {
        logger.error('Google Sign In Error:', error);
        return {
        data: null,
        error: error instanceof Error ? error : new Error('Google sign in failed')
      };
    }
  }

  private async createSessionFromUrl(url: string) {
    try {
      const { params } = QueryParams.getQueryParams(url);

      if (params?.error) {
        throw new Error(params.error_description || 'OAuth error');
      }

      // Extract tokens from the URL
      const { access_token, refresh_token } = params;

      if (!access_token) {
        throw new Error('No access token in OAuth response');
      }

      // Set the session with the tokens
      const { data, error } = await supabase.auth.setSession({
        access_token,
        refresh_token: refresh_token || '',
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      logger.error('Session creation error:', error);
      throw error;
    }
  }
}

export const googleAuth = new GoogleAuth();